import { Service } from '@roc/feature-app-core';

const url = {
  PAYOFF: '/api/v1/loan/requestPayoff',
  REQUEST_PAYOFF: '/api/v1/loan/requestPayoffV2',
  REQUEST_PAYOFF_V3: '/api/v1/loan/requestPayoffV3',
  GET_ACTIVE_PAYOFF: '/api/v1/loan/getActivePayoffByLoanId',
  SAVE_SERVICER_FEES: '/api/v1/loan/saveServicerFees',
  REQUEST_PAYOFF_ASSISTANCE: '/api/v1/loan/requestPayoffAssistance',
  ADD_SERVICER_ACCOUNTING_TEAM: '/api/v1/loan/addServicerAccountingTeam',
  GET_DEFAULT_ALLOCATION_DASHBOARD: '/api/v1/loan/getDefaultAllocationDashboard',
  GET_FORECLOSURE_INTEREST_ALLOCATION_DASHBOARD: '/api/v1/loan/getForeclosureInterestAllocation',
  EXTENSION: '/api/v1/loan/loanExtension/submit',
  EXTENSIONS_BYLOANID: '/api/v1/loan/getLoanExtensionsByLoanId',
  DOCUMENT_UPLOAD: '/api/v1/loan/uploadInsuranceDocument',
  CURRENT_EXTENSION: '/api/v1/loan/getCurrentExtensionForLoan',
  ELMSURE_TASKS: '/api/v1/loan/createExtensionTaskInElmsure',
  GET_PAYOFFS: '/api/v1/loan/getPayoffsByLoanId',
  EXTENSION_BY_ID: '/api/v1/loan/getLoanExtensionById',
  APPROVE_INSURANCE: '/api/v1/loan/approveInsuranceDoc',
  RETRACT_PAYOFF_EXTERNAL: '/api/v1/loan/retractPayoffExternal',
  RETRACT_PAYOFF: '/api/v1/loan/retractPayoff',
  UPDATE_LOAN_PAYOFF_DETAILS: '/api/v1/loan/updateLoanPayoffDetails',
  SUBMIT_PAYOFF_FORECLOSURE_LOAN: '/api/v1/loan/submitPayoffForclosureLoan',
  APPROVE_PAYOFF: '/api/v1/loan/approvePayoff',
  GET_PAYOFF: '/api/v1/loan/getPayoffById',
  GET_PAYOFF_CORRECTION: '/api/v1/loan/getPayoffCorrectionComment',
  GET_ON_HOLD_REASONS: '/api/v1/loan/getOnHoldReasons',
  PUT_ON_HOLD_PAYOFF: '/api/v1/loan/putOnHoldPayoff',
  NOTIFY_SERVICER_CORRECTION: '/api/v1/loan/notifyServicerForCorrection',
  UPDATE_REQUESTED_DATE: '/api/v1/loan/updateRequestedDate',
  NOTIFY_MANAGEMENT_FOR_PAYOFF_APROVAL: '/api/v1/loan/notifyManagementForPayoffApproval',
};

export class LoanRequestsService extends Service {
  requestPayoff(body: any) {
    return this.post(url.PAYOFF, body);
  }

  requestPayoffV2(body: any) {
    return this.put(url.REQUEST_PAYOFF, body);
  }

  requestPayoffV3(body: any) {
    return this.put(url.REQUEST_PAYOFF_V3, body);
  }

  getActivePayoffByLoanId(loanId: string) {
    return this.get(`${url.GET_ACTIVE_PAYOFF}/${loanId}`);
  }

  saveServicerFees(loanId: string, servicerFees: string) {
    return this.post(`${url.SAVE_SERVICER_FEES}`, { loanId, servicerFeesJson: servicerFees });
  }

  getPayOffsByLoanId(loanId: string) {
    return this.get(`${url.GET_PAYOFFS}/${loanId}`);
  }

  retractPayoffExternal(loanId: string, comments: string, loanPayoffDataId) {
    return this.put(`${url.RETRACT_PAYOFF_EXTERNAL}?loanId=${loanId}&comments=${comments}&loanPayoffDataId=${loanPayoffDataId}`, null);
  }

  retractPayoff(loanId: string, comments: string, loanPayoffDataId) {
    return this.put(`${url.RETRACT_PAYOFF}?loanId=${loanId}&comments=${comments}&loanPayoffDataId=${loanPayoffDataId}`, null);
  }

  updateLoanPayoffDetails(data: any) {
    return this.post(
      url.UPDATE_LOAN_PAYOFF_DETAILS,
      data
    );
  }

  submitForeclosurePayoff(data: any) {
    return this.post(
      url.SUBMIT_PAYOFF_FORECLOSURE_LOAN,
      data
    );
  }

  requestExtension(body: any) {
    return this.post(url.EXTENSION, body);
  }

  getExtensionsByLoanId(loanId) {
    return this.get(`${url.EXTENSIONS_BYLOANID}?loanId=${loanId}&count=50&start=1`);
  }

  getCurrentExtensionByLoanId(loanId) {
    return this.get(`${url.CURRENT_EXTENSION}?loanId=${loanId}`);
  }

  uploadDocument(file, loanId: number) {
    return this.post(`${url.DOCUMENT_UPLOAD}?loanId=${loanId}`, file);
  }

  createElmsureTask(body: any) {
    return this.post(url.ELMSURE_TASKS, body);
  }

  getExtensionById(loanExtensionId: string) {
    return this.get(`${url.EXTENSION_BY_ID}?loanExtensionId=${loanExtensionId}`);
  }

  approveInsurance(body: any) {
    return this.post(url.APPROVE_INSURANCE, body);
  }

  requestPayoffAssistance(loanId: string, loanPayoffDataId: number) {
    return this.get(`${url.REQUEST_PAYOFF_ASSISTANCE}?loanId=${loanId}&loanPayoffDataId=${loanPayoffDataId}`);
  }

  addServicerAccountingTeam(loanId: string) {
    return this.get(`${url.ADD_SERVICER_ACCOUNTING_TEAM}?loanId=${loanId}`);
  }

  getDefaultAllocationDashboard(loanId: string) {
    return this.get(`${url.GET_DEFAULT_ALLOCATION_DASHBOARD}?loanId=${loanId}`);
  }

  getForeclosureInterestAllocationDashboard(loanId: string) {
    return this.get(`${url.GET_FORECLOSURE_INTEREST_ALLOCATION_DASHBOARD}?loanId=${loanId}`);
  }

  approvePayoff(loanId, loanPayoffDataId, servicerPayoffAmount) {
    return this.put(`${url.APPROVE_PAYOFF}?loanId=${loanId}&loanPayoffDataId=${loanPayoffDataId}&servicerPayoffAmount=${servicerPayoffAmount}`, {});
  }

  getPayoffById(loanId: number, payoffId: number) {
    return this.get(url.GET_PAYOFF, { loanId, payoffId });
  }

  getPayoffCorrectionComment(loanId: number) {
    return this.get(url.GET_PAYOFF_CORRECTION, { loanId });
  }

  getOnHoldReasons() {
    return this.get(url.GET_ON_HOLD_REASONS);
  }

  putOnHoldPayoff(loanId: number, payoffId: number, reason: string, removeOnHold: boolean) {
    const endpointUrl = `${url.PUT_ON_HOLD_PAYOFF}?loanId=${loanId}&payoffId=${payoffId}&reason=${reason}&removeOnHold=${removeOnHold}`;
    return this.put(endpointUrl, {});
  }

  notifyServicerForCorrection(loanId: number, payoffId: number, comments: string) {
    const endpointUrl = `${url.NOTIFY_SERVICER_CORRECTION}?loanId=${loanId}&payoffId=${payoffId}`;
    return this.put(endpointUrl, { comments });
  }

  updateRequestedDate(loanId: number, payoffId: number, newRequestedDate: string) {
    const endpointUrl = `${url.UPDATE_REQUESTED_DATE}?loanId=${loanId}&payoffId=${payoffId}&newRequestedDate=${newRequestedDate}`;
    return this.put(endpointUrl, {});
  }

  notifyManagementForPayoffApproval(loanId: number, payoffId: number, comments: string) {
    const endpointUrl = `${url.NOTIFY_MANAGEMENT_FOR_PAYOFF_APROVAL}?loanId=${loanId}&payoffId=${payoffId}`;
    return this.put(endpointUrl, { comments });
  }
}
